import { Delete, MoreHoriz } from '@mui/icons-material';
import { styled, Typography } from '@mui/material';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Card as BaseCard, CardContent } from 'further-ui/components/Card';

const Card = styled(BaseCard)({
  height: '100%',
});
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Column, Divider, Row } from 'further-ui/components/Layout';
import Menu from 'further-ui/components/Menu';
import { useDisclosure } from 'further-ui/hooks';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';

type Props = {
  vehicleId?: string;
  bankAccountId?: string;
  bankOperationId?: string;
  description: string;
  counterparty: string;
  date: CalendarDay;
  spent?: number;
  received?: number;
  accountCurrency: CurrencyCode;
  showActions?: boolean;
};

const BankOperationDetails: React.FC<Props> = ({
  vehicleId,
  bankAccountId,
  bankOperationId,
  description,
  date,
  spent,
  received,
  accountCurrency,
  counterparty,
  showActions = true,
}) => {
  const deleteDisclosure = useDisclosure();

  const { data: canDeleteData } =
    api.bankAccount.canDeleteBankAccountOperations.useQuery(
      { vehicleId: vehicleId ?? '', bankAccountId: bankAccountId ?? '' },
      { enabled: !!vehicleId && !!bankAccountId && !!bankOperationId },
    );

  const deleteOperations =
    api.bankAccount.deleteBankAccountOperations.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Bank operation deleted.',
      }),
    );

  const canDelete = canDeleteData?.canDelete ?? true;
  const deleteDisabledReason = canDeleteData?.reason;

  const handleDelete = () => {
    if (!vehicleId || !bankAccountId || !bankOperationId) return;
    deleteOperations.mutate({
      vehicleId,
      bankAccountId,
      operationIds: [bankOperationId],
    });
    deleteDisclosure.onClose();
  };

  const spentAmount = numberToCurrencyString(
    spent ? Math.abs(spent) : undefined,
    {
      fallback: '--',
      currency: accountCurrency,
    },
  );
  const receivedAmount = numberToCurrencyString(received, {
    fallback: '--',
    currency: accountCurrency,
  });

  return (
    <>
      <Card variant="outlined">
        <CardContent size="md">
          <Column fullWidth spacing="sm">
            <Row spacing="md" fullWidth>
              <Column spacing="xs" fullWidth>
                <Typography variant="caption" fontWeight={500} color="grey.500">
                  {date.toReadable()}
                </Typography>
                <Typography variant="h6">{description}</Typography>
              </Column>
              <Column fullWidth>
                <Row spacing="xs">
                  <Column spacing="xs" fullWidth>
                    <Typography
                      variant="caption"
                      fontWeight={500}
                      color="grey.500"
                    >
                      Spent
                    </Typography>
                    <Typography
                      variant="h6"
                      color={spent ? 'inherit' : 'grey.500'}
                    >
                      {spentAmount}
                    </Typography>
                  </Column>
                  <Column spacing="xs" fullWidth>
                    <Typography
                      variant="caption"
                      fontWeight={500}
                      color="grey.500"
                    >
                      Received
                    </Typography>
                    <Typography
                      variant="h6"
                      color={received ? 'inherit' : 'grey.500'}
                    >
                      {receivedAmount}
                    </Typography>
                  </Column>
                  {showActions && bankOperationId && (
                    <Column>
                      <Menu
                        handleVariant="default"
                        handleSize="small"
                        handleEdge="top"
                        handleIcon={MoreHoriz}
                        items={[
                          {
                            label: 'Delete',
                            icon: Delete,
                            disabled: !canDelete,
                            tooltip: !canDelete
                              ? deleteDisabledReason
                              : undefined,
                            onClick: deleteDisclosure.onOpen,
                          },
                        ]}
                      />
                    </Column>
                  )}
                </Row>
              </Column>
            </Row>
            <Divider />
            <Column spacing="xs">
              <Typography variant="body2">{counterparty}</Typography>
            </Column>
          </Column>
        </CardContent>
      </Card>
      {showActions && bankOperationId && (
        <ConfirmDialog
          open={deleteDisclosure.isOpen}
          onClose={deleteDisclosure.onClose}
          title="Are you sure you want to delete this transaction?"
          content="This action cannot be undone."
          maxWidth="sm"
          actions={{
            confirm: {
              onClick: handleDelete,
              label: 'Delete',
            },
          }}
        />
      )}
    </>
  );
};

export default BankOperationDetails;
